/**
 * Centralized status color system
 * Single source of truth for all user status colors across the application
 */

export type UserStatus = 'available' | 'busy' | 'dnd';

export interface StatusColorConfig {
  text: string;
  bg: string;
  border: string;
}

export const STATUS_COLORS: Record<UserStatus, StatusColorConfig> = {
  available: {
    text: 'text-green-500',        // Actual green for available status
    bg: 'bg-green-500/20',
    border: 'border-green-500/30'
  },
  busy: {
    text: 'text-game-golden',      // Golden yellow for busy
    bg: 'bg-game-golden/20',
    border: 'border-game-golden/30'
  },
  dnd: {
    text: 'text-game-orange',      // Orange for do not disturb
    bg: 'bg-game-orange/20',
    border: 'border-game-orange/30'
  }
} as const;

/**
 * Get status color configuration for a given status
 * @param status - User status string
 * @returns StatusColorConfig object with text, bg, and border classes
 */
export function getStatusColor(status: string): StatusColorConfig {
  const normalizedStatus = status?.toLowerCase() as UserStatus;
  return STATUS_COLORS[normalizedStatus] || STATUS_COLORS.available;
}

/**
 * Narrow a free-form status string to the three the UI knows about.
 * Every consumer already falls back to `available` for anything else, so this
 * only makes that fallback visible to the type checker.
 */
export function toUserStatus(status: string | undefined | null): UserStatus {
  const normalized = status?.toLowerCase();
  return normalized && normalized in STATUS_COLORS ? (normalized as UserStatus) : 'available';
}

/**
 * Get just the text color class for a status (most common use case)
 * @param status - User status string  
 * @returns Tailwind text color class
 */
export function getStatusTextColor(status: string): string {
  return getStatusColor(status).text;
}

/**
 * Get status label for display
 * @param status - User status string
 * @returns Human-readable status label
 */
export function getStatusLabel(status: string): string {
  switch (status?.toLowerCase()) {
    case 'available':
      return 'Available';
    case 'busy':
      return 'Busy';
    case 'dnd':
      return 'Do Not Disturb';
    default:
      return 'Available';
  }
}